import { STARWEAVE_NODES } from '~/data/starweave'
import { ELEMENTS, type Creature, type Mission, type RunState, type Stats } from '~/types/game'
import type { StarweaveNodeState, StarweaveRunState } from '~/types/starweave'

export function hasStarweave(run: RunState | null | undefined, id: string): boolean {
  return !!run && run.mode !== 'daily' && typeof run.puzzleLevel !== 'number'
    && !!run.starweave?.owned.includes(id)
}

export function createStarweaveRunState(owned: readonly string[]): StarweaveRunState {
  const valid = new Set(STARWEAVE_NODES.map(node => node.id))
  return { owned: [...new Set(owned)].filter(id => valid.has(id)) }
}

export function starweaveNodeKey(run: RunState): string {
  return `${run.act}:${run.activeNodeId ?? run.position ?? 'camp'}`
}

export function freshStarweaveNode(key: string): StarweaveNodeState {
  return { key, momentumIds: [], courierIds: [], riskWins: 0, pairedUsed: false,
    failureXpUsed: false, failureReductionUsed: false, goldRefreshUsed: false,
    windfallUsed: false, hadFailure: false, regroupUsed: false, homewardUsed: false, mentorshipXp: 0 }
}

/** Read-only previews never initialize or consume counters. */
export function starweaveNode(run: RunState | null | undefined): StarweaveNodeState | undefined {
  return run?.starweave?.node?.key === (run && starweaveNodeKey(run)) ? run?.starweave?.node : undefined
}

export function ensureStarweaveNode(run: RunState): StarweaveNodeState | undefined {
  if (!run.starweave || run.mode === 'daily' || typeof run.puzzleLevel === 'number') return
  if (!starweaveNode(run)) run.starweave.node = freshStarweaveNode(starweaveNodeKey(run))
  return run.starweave.node
}

/** 🔮 v7.69 (per Javi): Master Collector's set bonus - all five Element Charms owned. */
export function collectorFullSet(run?: RunState | null): boolean {
  return hasStarweave(run, 'nova-discovery') && ELEMENTS.every(element => run!.relics.includes(`relic-${CHARM_STEM[element]}-charm`))
}
const CHARM_STEM: Record<string, string> = { fire: 'flame', water: 'tide', grass: 'root', lightning: 'storm', earth: 'stone' }

export function starweaveSpeedBonus(creature: Creature, run?: RunState | null): number {
  const node = starweaveNode(run)
  return (hasStarweave(run, 'nova-homeward') ? 2 : 0)
    + (collectorFullSet(run) ? 2 : 0)
    + (hasStarweave(run, 'command-left') && node?.courierIds.includes(creature.id) ? 1 : 0)
    + (hasStarweave(run, 'command-anchor') && node?.readyCreatureId === creature.id ? 1 : 0)
}

export function starweaveFightBonus(creature: Creature, run?: RunState | null): number {
  const node = starweaveNode(run)
  return (hasStarweave(run, 'command-spark') && node?.momentumIds.includes(creature.id) ? 1 : 0)
    + (hasStarweave(run, 'command-anchor') && node?.readyCreatureId === creature.id ? 1 : 0)
}

/** Additional node buffs this proposed dispatch would turn on, above its current buffs. */
export function starweaveDispatchBonus(creature: Creature, team: Creature[], run?: RunState | null) {
  const node = starweaveNode(run)
  const ready = hasStarweave(run, 'command-anchor') && !node?.readyCreatureId && team[0]?.id === creature.id ? 1 : 0
  const solo = hasStarweave(run, 'command-left') && team.length === 1 && !node?.courierIds.includes(creature.id) ? 1 : 0
  return { speed: ready + solo, fight: ready }
}

export function starweaveOnDispatch(run: RunState, team: Creature[]) {
  const node = ensureStarweaveNode(run)
  if (!node || !team.length) return
  if (hasStarweave(run, 'command-anchor') && !node.readyCreatureId) node.readyCreatureId = team[0]!.id
  if (hasStarweave(run, 'command-left') && team.length === 1 && !node.courierIds.includes(team[0]!.id)) node.courierIds.push(team[0]!.id)
}

export function starweaveElementBonus(creature: Creature, run?: RunState | null): Stats {
  const bonus: Stats = { fire: 0, water: 0, grass: 0, lightning: 0, earth: 0 }
  if (!run?.starweave || run.mode === 'daily' || typeof run.puzzleLevel === 'number') return bonus
  for (const e of ELEMENTS) bonus[e] = run.starweave.elementalBonuses?.[creature.id]?.[e] ?? 0
  if (hasStarweave(run, 'attunement-anchor') && run.starweave.firstDraftedCreatureId === creature.id) {
    const open = ELEMENTS.filter(e => creature.stats[e] + bonus[e] > 0)
    if (open.length) {
      // Snapshot both choices before applying either bonus; deterministic ties.
      const low = open.reduce((a, b) => creature.stats[b] + bonus[b] < creature.stats[a] + bonus[a] ? b : a)
      const high = open.reduce((a, b) => creature.stats[b] + bonus[b] > creature.stats[a] + bonus[a] ? b : a)
      bonus[low]++
      if (creature.surgePrimed) bonus[high]++
    }
  }
  return bonus
}

export function starweaveWindfallChance(run?: RunState | null): number {
  return (hasStarweave(run, 'fortune-spark') ? 5 : 0)
    + (hasStarweave(run, 'fortune-right') ? 5 : 0)
    + (hasStarweave(run, 'fortune-anchor') ? 10 : 0)
}

/** The existing relic payload and every talent payload are combined before Jackpot doubles. */
export function starweaveWindfallGold(run: RunState, relicGold: number): number {
  const node = ensureStarweaveNode(run)
  let gold = relicGold + (hasStarweave(run, 'fortune-spark') ? 5 : 0)
  if (node && hasStarweave(run, 'fortune-right') && !node.windfallUsed) {
    node.windfallUsed = true
    gold += 10
  }
  return gold * (hasStarweave(run, 'fortune-anchor') ? 2 : 1)
}

export function highestStarweaveLevel(run: RunState): number {
  return Math.max(0, ...run.roster.map(c => c.level))
}

function cureOne(creature: Creature) {
  creature.injuries = Math.max(0, (creature.injuries ?? (creature.injured ? 1 : 0)) - 1)
  creature.injured = creature.injuries > 0
}

/** Called only after an actual damage payment; HP costs must not call this. */
export function starweaveAfterDamage(run: RunState): boolean {
  if (run.hp <= 0 && hasStarweave(run, 'nova-dawn') && !run.starweave!.secondDawnUsed) {
    run.starweave!.secondDawnUsed = true
    run.hp = run.maxHp // 🌅 v7.69 (per Javi): a full second dawn (was 30%)
    for (const c of run.roster) { c.injuries = 0; c.injured = false }
    return true
  }
  if (run.hp > 0 && run.hp < run.maxHp * 0.25 && hasStarweave(run, 'resolve-signature') && run.starweave!.lastReserveAct !== run.act) {
    run.starweave!.lastReserveAct = run.act
    run.hp = Math.min(run.maxHp, run.hp + highestStarweaveLevel(run))
  }
  return false
}

/** Consumes node limits synchronously, before XP can trigger a level-up chain. */
export function starweaveOnSuccess(run: RunState, mission: Mission, team: Creature[]): number {
  const node = ensureStarweaveNode(run)
  if (!node || mission.training || !team.length) return 0
  if (hasStarweave(run, 'command-spark')) for (const c of team) if (!node.momentumIds.includes(c.id)) node.momentumIds.push(c.id)
  if (hasStarweave(run, 'attunement-right') && (mission.assignedChance ?? 100) < 60 && node.riskWins < 3) {
    node.riskWins++
    run.starweave!.elementalBonuses ??= {}
    for (const c of team) {
      const bonus: Partial<Stats> = run.starweave!.elementalBonuses[c.id] ??= {}
      const open = ELEMENTS.filter(e => c.stats[e] + (bonus[e] ?? 0) > 0)
      if (!open.length) continue
      const low = open.reduce((a, b) => c.stats[b] + (bonus[b] ?? 0) < c.stats[a] + (bonus[a] ?? 0) ? b : a)
      bonus[low] = (bonus[low] ?? 0) + 1
    }
  }
  if (hasStarweave(run, 'command-signature') && mission.goldReward && !node.goldRefreshUsed) {
    node.goldRefreshUsed = true
    for (const c of run.roster) { c.surgeCooldown = 0; if (!c.available) c.surgeRallied = true }
  }
  if (hasStarweave(run, 'resolve-anchor') && node.hadFailure && !node.regroupUsed) {
    node.regroupUsed = true
    run.hp = Math.min(run.maxHp, run.hp + highestStarweaveLevel(run))
    for (const c of team) cureOne(c)
  }
  if (hasStarweave(run, 'growth-left') && team.length === 2 && !node.pairedUsed) {
    node.pairedUsed = true
    return 5
  }
  return 0
}

export function starweaveFailureReduction(run?: RunState | null): number {
  return hasStarweave(run, 'resolve-left') && !starweaveNode(run)?.failureReductionUsed ? 1 : 0
}

/** 🎩 v7.79: what a failure PAYS (Regroup armed; Lessons in Defeat's XP, once a node) - also paid when Mr. Khool's
 * Composure turns a failed roll into a win (failureBonuses in utils/battle). A rescue arms Regroup only AFTER its own
 * win (armRegroup false here, starweaveArmRegroup later), so the rescued win cannot spend it. */
export function starweaveFailureBonus(run: RunState, armRegroup = true): number {
  const node = ensureStarweaveNode(run)
  if (!node) return 0
  if (armRegroup) node.hadFailure = true
  if (hasStarweave(run, 'growth-right') && !node.failureXpUsed) {
    node.failureXpUsed = true
    return 10
  }
  return 0
}

/** 🎩 v7.79: Regroup armed for the next win - a Composure rescue's, stamped after the rescued win's own hooks. */
export function starweaveArmRegroup(run: RunState): void {
  const node = ensureStarweaveNode(run)
  if (node) node.hadFailure = true
}

/** 🎩 v7.79: what a failure COSTS here - Controlled Failure's -1 damage is spent only by a failure that lands. */
export function starweaveFailureCost(run: RunState): void {
  const node = ensureStarweaveNode(run)
  if (node && hasStarweave(run, 'resolve-left')) node.failureReductionUsed = true
}

export function starweaveOnFailure(run: RunState): number {
  starweaveFailureCost(run)
  return starweaveFailureBonus(run)
}

export function starweaveOnFailedReturn(run: RunState, ids: readonly string[]) {
  const node = ensureStarweaveNode(run)
  if (!node || node.homewardUsed || !hasStarweave(run, 'nova-homeward')) return
  node.homewardUsed = true
  for (const c of run.roster) if (ids.includes(c.id)) cureOne(c)
}
